// app/javascript/components/Notifications/NotificationPanel.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Button,
  Collapse,
  IconButton,
  Link as MuiLink,
  Tooltip,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import { getUserNotificationsPage, markNotificationSeen } from "../../api/notifications";
import { isDarkMode } from "../../utils/constants";
import MessageSegments from "../Chat/MessageSegments";
import {
  agentTaskNotificationContent,
  agentTaskNotificationTextColor,
} from "./notificationContent";

function AgentTaskNotificationContent({ notification }) {
  const content = agentTaskNotificationContent(notification);
  if (!content) return null;

  return (
    <Box
      data-user-notification-agent-task-content
      sx={{ color: agentTaskNotificationTextColor(isDarkMode) }}
    >
      <MessageSegments
        msgId={`user-notification-${notification?.id || "unknown"}`}
        raw={content}
        isDark={isDarkMode}
        isStreaming={false}
      />
    </Box>
  );
}

export default function NotificationPanel({
  onClose = null,
  onUnreadCountChange = () => {},
  variant = "legacy",
}) {
  const [items, setItems] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [workspaceFilter, setWorkspaceFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const itemsRef = useRef([]);
  const isWorkspace = variant === "workspace";

  const PAGE = 30;

  const load = async ({ reset = false } = {}) => {
    if (loading) return;
    setLoading(true);

    const nextOffset = reset ? 0 : offset;
    const page = await getUserNotificationsPage({ limit: PAGE, offset: nextOffset });

    if (page?.error) {
      setLoading(false);
      return;
    }

    const arr = Array.isArray(page) ? page : [];
    setItems((prev) => (reset ? arr : [...prev, ...arr]));
    setOffset(nextOffset + arr.length);
    setHasMore(arr.length === PAGE);
    setLoading(false);
  };

  useEffect(() => {
    load({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    itemsRef.current = Array.isArray(items) ? items : [];
  }, [items]);

  useEffect(() => {
    const handler = (event) => {
      const nextNotification = event?.detail?.notification;
      if (!nextNotification?.id) return;

      const currentItems = Array.isArray(itemsRef.current) ? itemsRef.current : [];
      const existingIndex = currentItems.findIndex(
        (item) => String(item.id) === String(nextNotification.id)
      );

      if (existingIndex >= 0) {
        const nextItems = currentItems.map((item) =>
          String(item.id) === String(nextNotification.id)
            ? { ...item, ...nextNotification }
            : item
        );
        itemsRef.current = nextItems;
        setItems(nextItems);
        return;
      }

      const nextItems = [nextNotification, ...currentItems];
      itemsRef.current = nextItems;
      setItems(nextItems);
      setOffset((prev) => prev + 1);
    };

    window.addEventListener("user-notifications:updated", handler);
    return () => window.removeEventListener("user-notifications:updated", handler);
  }, []);

  // ----- datetime formatting rules you specified
  const fmtWhen = (n) => {
    const iso = n?.createdAt || n?.created_at || n?.updatedAt || n?.updated_at;
    if (!iso) return "";

    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";

    const now = new Date();

    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);

    const startOfDayD = new Date(d.getFullYear(), d.getMonth(), d.getDate());

    // Today => time only
    if (startOfDayD.getTime() === startOfToday.getTime()) {
      return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    }

    // Yesterday => literal "Yesterday"
    if (startOfDayD.getTime() === startOfYesterday.getTime()) {
      return "Yesterday";
    }

    // Within last 7 days => weekday
    const msSinceStartOfD = startOfToday.getTime() - startOfDayD.getTime();
    const daysAgo = Math.floor(msSinceStartOfD / (24 * 60 * 60 * 1000));
    if (daysAgo >= 2 && daysAgo <= 6) {
      return d.toLocaleDateString(undefined, { weekday: "short" }); // Mon, Tue, ...
    }

    // Older => full date/time (your existing style)
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const fmtWorkspaceWhen = (n) => {
    const iso = n?.createdAt || n?.created_at || n?.updatedAt || n?.updated_at;
    if (!iso) return "";

    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const daysAgo = Math.floor(
      (startOfToday.getTime() - startOfDate.getTime()) / (24 * 60 * 60 * 1000)
    );

    if (daysAgo === 0) {
      return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    }
    if (daysAgo === 1) return "Yesterday";
    if (daysAgo >= 2 && daysAgo <= 6) {
      return date.toLocaleDateString(undefined, { weekday: "short" });
    }

    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      ...(date.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
    });
  };

  const fmtFullWhen = (n) => {
    const iso = n?.createdAt || n?.created_at || n?.updatedAt || n?.updated_at;
    if (!iso) return "";

    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";

    return date.toLocaleString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  // ----- rich text renderer: supports [label](url) + bare URLs + emails, preserves newlines
  const MD_LINK_RE = useMemo(
    () => /\[([^\]]+)\]\((https?:\/\/[^\s)]+|www\.[^\s)]+)\)/gi,
    []
  );
  const EMAIL_RE = useMemo(
    () => /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi,
    []
  );
  const URL_OR_EMAIL_RE = useMemo(
    () => /(\bhttps?:\/\/[^\s]+|\bwww\.[^\s]+|\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b)/gi,
    []
  );

  function splitTrailingPunct(token) {
    // E.g. "andrew@x.com)." => "andrew@x.com" + ")."
    const m = token.match(/^(.*?)([)\].,!?;:]+)$/);
    return m ? { core: m[1], tail: m[2] } : { core: token, tail: "" };
  }

  const linkSx = {
    color: isDarkMode ? "#60a5fa" : "#2563eb",
    textDecoration: "underline",
    fontWeight: 800,
    wordBreak: "break-word",
    display: "inline-block",
    px: "3px",
    py: "1px",
    borderRadius: "6px",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
      bgcolor: isDarkMode ? "rgba(96,165,250,0.12)" : "rgba(37,99,235,0.10)",
    },
  };

  const renderRichText = (text, { workspace = false } = {}) => {
    const s = (text ?? "").toString();
    if (!s) return null;

    // 1) turn markdown links into tokens
    const chunks = [];
    let last = 0;
    let m;
    while ((m = MD_LINK_RE.exec(s)) !== null) {
      const [whole, label, urlRaw] = m;
      const start = m.index;
      if (start > last) chunks.push({ type: "text", value: s.slice(last, start) });

      const href = urlRaw.startsWith("http") ? urlRaw : `https://${urlRaw}`;
      chunks.push({ type: "link", label, href, key: `mdl-${start}` });

      last = start + whole.length;
    }
    if (last < s.length) chunks.push({ type: "text", value: s.slice(last) });

    // 2) within text chunks, linkify URLs + emails
    const nodes = [];
    chunks.forEach((c, i) => {
      if (c.type === "link") {
        nodes.push(
          <MuiLink
            key={c.key}
            href={c.href}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            sx={workspace ? {
              ...linkSx,
              display: "inline",
              px: 0,
              py: 0,
              borderRadius: 0,
              fontWeight: 600,
            } : linkSx}
          >
            {c.label}
          </MuiLink>
        );
        return;
      }

      const t = c.value;
      let lastIdx = 0;
      let mm;

      URL_OR_EMAIL_RE.lastIndex = 0;
      while ((mm = URL_OR_EMAIL_RE.exec(t)) !== null) {
        const raw = mm[0];
        const start = mm.index;

        if (start > lastIdx) nodes.push(t.slice(lastIdx, start));

        const { core, tail } = splitTrailingPunct(raw);

        const isEmail = EMAIL_RE.test(core);
        EMAIL_RE.lastIndex = 0;

        let href = core;
        if (isEmail) {
          href = `mailto:${core}`;
        } else {
          href = core.startsWith("http") ? core : `https://${core}`;
        }

        nodes.push(
          <MuiLink
            key={`tok-${i}-${start}`}
            href={href}
            target={isEmail ? undefined : "_blank"}
            rel={isEmail ? undefined : "noreferrer"}
            onClick={(e) => e.stopPropagation()}
            sx={workspace ? {
              ...linkSx,
              display: "inline",
              px: 0,
              py: 0,
              borderRadius: 0,
              fontWeight: 600,
            } : linkSx}
          >
            {core}
          </MuiLink>
        );

        if (tail) nodes.push(tail);

        lastIdx = start + raw.length;
      }

      if (lastIdx < t.length) nodes.push(t.slice(lastIdx));
    });

    return (
      <Typography
        sx={{
          fontSize: workspace ? 14.25 : 14,
          lineHeight: workspace ? 1.58 : undefined,
          whiteSpace: "pre-wrap",
          color: workspace
            ? (isDarkMode ? "rgba(245,247,250,0.82)" : "#263041")
            : (isDarkMode ? "#e5e7eb" : "#111827"),
        }}
      >
        {nodes}
      </Typography>
    );
  };

  const normalizedActionUrl = (actionUrl) => {
    const raw = (actionUrl || "").toString().trim();
    if (!raw) return "";
    if (/^(https?:\/\/|mailto:|tel:|sms:)/i.test(raw)) return raw;
    return raw.startsWith("/") ? raw : `/${raw.replace(/^\/+/, "")}`;
  };

  const markSeenIfNeeded = async (n) => {
    if (n.seenAt) return;

    const nowIso = new Date().toISOString();

    // optimistic
    setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, seenAt: nowIso } : x)));

    try {
      await markNotificationSeen(n.id);
      onUnreadCountChange();
    } catch {
      // revert on failure (optional)
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, seenAt: null } : x)));
    }
  };

  const onToggle = async (n) => {
    const willOpen = expandedId !== n.id;
    setExpandedId(willOpen ? n.id : null);
    if (willOpen) await markSeenIfNeeded(n);
  };

  const ScrollableMessageBody = ({ children, maxHeight = 400, fadeBg }) => {
    const scrollRef = useRef(null);
    const [canScrollDown, setCanScrollDown] = useState(false);

    const updateScrollState = () => {
      const el = scrollRef.current;
      if (!el) return;
      const maxScroll = el.scrollHeight - el.clientHeight;
      const scrollTop = el.scrollTop;
      setCanScrollDown(maxScroll > 1 && scrollTop < maxScroll - 1);
    };

    useEffect(() => {
      updateScrollState();
    }, [children]);

    return (
      <Box sx={{ position: "relative" }}>
        <Box
          ref={scrollRef}
          onScroll={updateScrollState}
          sx={{
            maxHeight,
            overflowY: "auto",
            pr: 0.5,
            pb: 1,
          }}
        >
          {children}
        </Box>

        {canScrollDown && (
          <Box
            sx={{
              pointerEvents: "none",
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: 28,
              background: `linear-gradient(180deg, rgba(0,0,0,0) 0%, ${fadeBg} 80%)`,
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              pb: 0.25,
            }}
          >
            <Typography sx={{ fontSize: 14, color: isDarkMode ? "#9ca3af" : "#6b7280" }}>
              ▾
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const workspaceColors = {
    surface: isDarkMode ? "#17191E" : "#FFFFFF",
    surfaceHover: isDarkMode ? "#1D2026" : "#F7F9FC",
    border: isDarkMode ? "rgba(235,240,250,0.09)" : "rgba(17,24,39,0.10)",
    text: isDarkMode ? "#F5F7FA" : "#121417",
    muted: isDarkMode ? "rgba(245,247,250,0.65)" : "rgba(18,20,23,0.62)",
    subtle: isDarkMode ? "rgba(245,247,250,0.42)" : "rgba(18,20,23,0.46)",
    accent: isDarkMode ? "#7AA2FF" : "#2563EB",
    accentSoft: isDarkMode ? "rgba(122,162,255,0.12)" : "rgba(37,99,235,0.08)",
  };

  const unreadTotal = items.filter((item) => !item.seenAt).length;
  const workspaceItems = items.filter((item) => (
    workspaceFilter === "all" || !item.seenAt || item.id === expandedId
  ));

  const workspacePreview = (notification) => {
    const content = agentTaskNotificationContent(notification)
      || notification?.text
      || notification?.body
      || "";

    return content
      .toString()
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
      .replace(/[*_`>#]+/g, "")
      .replace(/\s+/g, " ")
      .trim();
  };

  const selectWorkspaceFilter = (filterId) => {
    if (filterId === "unread") {
      const expandedItem = items.find((item) => item.id === expandedId);
      if (expandedItem?.seenAt) setExpandedId(null);
    }
    setWorkspaceFilter(filterId);
  };

  if (isWorkspace) {
    return (
      <Box
        data-workspace-inbox-panel
        sx={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          bgcolor: workspaceColors.surface,
          color: workspaceColors.text,
        }}
      >
        <Box
          data-workspace-inbox-header
          sx={{
            px: { xs: 2, sm: 2.5 },
            pt: { xs: 2, sm: 2.25 },
            pb: 1.5,
            flexShrink: 0,
            borderBottom: `1px solid ${workspaceColors.border}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                <Typography sx={{ fontSize: 22, lineHeight: 1.3, fontWeight: 700 }}>
                  Inbox
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: workspaceColors.muted }}>
                  {unreadTotal ? `${unreadTotal} unread` : "All caught up"}
                </Typography>
              </Box>
            </Box>

            {onClose ? (
              <Tooltip title="Close inbox" disableInteractive>
                <IconButton
                  data-workspace-inbox-close
                  onClick={onClose}
                  aria-label="Close inbox"
                  size="small"
                  sx={{
                    mt: 0.25,
                    width: 34,
                    height: 34,
                    color: workspaceColors.muted,
                    border: `1px solid ${workspaceColors.border}`,
                    "&:hover": { bgcolor: workspaceColors.surfaceHover },
                  }}
                >
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>

          <Box
            aria-label="Inbox filter"
            sx={{
              mt: 1.75,
              display: "inline-flex",
              gap: 0.5,
              p: 0.4,
              borderRadius: 2,
              bgcolor: isDarkMode ? "rgba(255,255,255,0.035)" : "#F1F3F6",
            }}
          >
            {[
              { id: "all", label: "All" },
              { id: "unread", label: unreadTotal ? `Unread ${unreadTotal}` : "Unread" },
            ].map((filter) => {
              const selected = workspaceFilter === filter.id;
              return (
                <Button
                  key={filter.id}
                  data-workspace-inbox-filter={filter.id}
                  onClick={() => selectWorkspaceFilter(filter.id)}
                  aria-pressed={selected}
                  size="small"
                  sx={{
                    minWidth: 64,
                    px: 1.25,
                    py: 0.45,
                    borderRadius: 1.5,
                    textTransform: "none",
                    fontSize: 12.5,
                    lineHeight: 1.45,
                    fontWeight: 600,
                    color: selected ? workspaceColors.text : workspaceColors.muted,
                    bgcolor: selected ? workspaceColors.surface : "transparent",
                    boxShadow: selected
                      ? (isDarkMode ? "0 1px 3px rgba(0,0,0,0.35)" : "0 1px 3px rgba(15,23,42,0.10)")
                      : "none",
                    "&:hover": {
                      bgcolor: selected ? workspaceColors.surface : workspaceColors.surfaceHover,
                    },
                  }}
                >
                  {filter.label}
                </Button>
              );
            })}
          </Box>
        </Box>

        <Box
          data-workspace-inbox-scroll-region
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            overscrollBehavior: "contain",
            scrollbarGutter: "stable",
          }}
        >
          {loading && items.length === 0 ? (
            <Box sx={{ px: 2.5, py: 3 }}>
              <Typography sx={{ fontSize: 13, color: workspaceColors.muted }}>
                Loading inbox...
              </Typography>
            </Box>
          ) : null}

          {!loading && workspaceItems.length === 0 ? (
            <Box
              data-workspace-inbox-empty
              sx={{
                px: 4,
                py: 8,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
              }}
            >
              <Box
                sx={{
                  mb: 1.5,
                  width: 42,
                  height: 42,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: "50%",
                  bgcolor: workspaceColors.accentSoft,
                  color: workspaceColors.accent,
                }}
              >
                <NotificationsNoneRoundedIcon fontSize="small" />
              </Box>
              <Typography sx={{ fontSize: 15, fontWeight: 650 }}>
                {workspaceFilter === "unread" ? "No unread updates" : "Your inbox is clear"}
              </Typography>
              <Typography sx={{ mt: 0.5, maxWidth: 280, fontSize: 13, lineHeight: 1.5, color: workspaceColors.muted }}>
                Market alerts, task updates, and reports will show up here.
              </Typography>
            </Box>
          ) : null}

          {workspaceItems.map((n, index) => {
            const isExpanded = expandedId === n.id;
            const isUnread = !n.seenAt;
            const agentTaskContent = agentTaskNotificationContent(n);
            const preview = workspacePreview(n);

            return (
              <Box
                key={n.id}
                data-workspace-inbox-item
                data-user-notification-id={n.id}
                data-user-notification-kind={n.kind || "notification"}
                data-unread={isUnread ? "true" : "false"}
                sx={{
                  position: "relative",
                  borderBottom: `1px solid ${workspaceColors.border}`,
                  bgcolor: isUnread ? workspaceColors.accentSoft : "transparent",
                  transition: "background-color 140ms ease",
                  "&:hover": { bgcolor: workspaceColors.surfaceHover },
                  "&::before": isUnread ? {
                    content: '""',
                    position: "absolute",
                    top: 18,
                    left: { xs: 8, sm: 10 },
                    width: 3,
                    height: 18,
                    borderRadius: 999,
                    bgcolor: workspaceColors.accent,
                  } : undefined,
                }}
              >
                <Box
                  data-workspace-inbox-toggle
                  data-workspace-inbox-toggle-index={index}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  onClick={() => onToggle(n)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onToggle(n);
                    }
                  }}
                  sx={{
                    px: { xs: 2.25, sm: 2.5 },
                    py: 1.65,
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) auto",
                    columnGap: 1.25,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 14.5,
                        lineHeight: 1.35,
                        fontWeight: isUnread ? 700 : 600,
                        color: workspaceColors.text,
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 2,
                        overflow: "hidden",
                      }}
                    >
                      {n.title || "Notification"}
                    </Typography>
                    {!isExpanded && preview ? (
                      <Typography
                        sx={{
                          mt: 0.65,
                          fontSize: 12.75,
                          lineHeight: 1.45,
                          color: workspaceColors.muted,
                          display: "-webkit-box",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: 2,
                          overflow: "hidden",
                        }}
                      >
                        {preview}
                      </Typography>
                    ) : null}
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.45 }}>
                    <Tooltip title={fmtFullWhen(n)} placement="top" arrow>
                      <Typography
                        aria-label={`Received ${fmtFullWhen(n)}`}
                        sx={{
                          pt: 0.1,
                          fontSize: 11.5,
                          lineHeight: 1.4,
                          color: workspaceColors.subtle,
                          whiteSpace: "nowrap",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {fmtWorkspaceWhen(n)}
                      </Typography>
                    </Tooltip>
                    <KeyboardArrowDownRoundedIcon
                      sx={{
                        mt: -0.2,
                        fontSize: 20,
                        color: workspaceColors.subtle,
                        transform: isExpanded ? "rotate(180deg)" : "none",
                        transition: "transform 160ms ease",
                      }}
                    />
                  </Box>
                </Box>

                <Collapse in={isExpanded} timeout={160} unmountOnExit>
                  <Box
                    data-workspace-inbox-detail
                    sx={{
                      mx: { xs: 2.25, sm: 2.5 },
                      mb: 2,
                      pl: 1.5,
                      borderLeft: `2px solid ${workspaceColors.accent}`,
                      cursor: "text",
                      userSelect: "text",
                    }}
                  >
                    {agentTaskContent ? (
                      <AgentTaskNotificationContent notification={n} />
                    ) : (
                      <>
                        {n.text && renderRichText(n.text, { workspace: true })}
                        {n.body && (
                          <Box sx={{ mt: n.text ? 1.25 : 0 }}>
                            {renderRichText(n.body, { workspace: true })}
                          </Box>
                        )}
                      </>
                    )}

                    {n.actionUrl ? (
                      <Button
                        component="a"
                        href={normalizedActionUrl(n.actionUrl)}
                        target={
                          /^https?:\/\//i.test(normalizedActionUrl(n.actionUrl))
                            ? "_blank"
                            : undefined
                        }
                        rel={
                          /^https?:\/\//i.test(normalizedActionUrl(n.actionUrl))
                            ? "noreferrer"
                            : undefined
                        }
                        variant="contained"
                        size="small"
                        sx={{
                          mt: 1.5,
                          px: 1.5,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 650,
                          boxShadow: "none",
                        }}
                      >
                        Open
                      </Button>
                    ) : null}
                  </Box>
                </Collapse>
              </Box>
            );
          })}

          {hasMore ? (
            <Box sx={{ px: 2.5, py: 2 }}>
              <Button
                data-workspace-inbox-load-more
                onClick={() => load({ reset: false })}
                disabled={loading}
                variant="outlined"
                fullWidth
                sx={{
                  py: 0.85,
                  borderRadius: 2,
                  borderColor: workspaceColors.border,
                  color: workspaceColors.text,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                {loading ? "Loading..." : "Load older updates"}
              </Button>
            </Box>
          ) : null}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2, py: 1.5, height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Box
        sx={{
          mb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: isDarkMode ? "#fff" : "#111" }}>
          Inbox
        </Typography>
        {onClose ? (
          <Tooltip title="Close inbox" disableInteractive>
            <IconButton
              data-workspace-inbox-close
              onClick={onClose}
              aria-label="Close inbox"
              size="small"
              sx={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>

      <Divider sx={{ mb: 1.5, borderColor: isDarkMode ? "#2f2f2f" : "#e7e7e7" }} />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }}>
        {items.map((n) => {
          const isExpanded = expandedId === n.id;
          const isUnread = !n.seenAt;
          const agentTaskContent = agentTaskNotificationContent(n);
          const cardBg = isUnread ? (isDarkMode ? "#111827" : "#EBEEF3") : (isDarkMode ? "#121212" : "#FFFFFF");

          return (
            <Box
              key={n.id}
              data-user-notification-id={n.id}
              data-user-notification-kind={n.kind || "notification"}
              sx={{ mb: 0.75 }}
            >
              {/* Non-clickable card container */}
              <Box
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: isDarkMode ? "#2f2f2f" : "#e7e7e7",
                  bgcolor: cardBg,
                  "&:hover": {
                    bgcolor: isUnread
                      ? (isDarkMode ? "#0f172a" : "#EEF6FF")
                      : (isDarkMode ? "#161616" : "#FAFAFA"),
                  },
                }}
              >
                {/* header row: ONLY thing that toggles */}
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => onToggle(n)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onToggle(n);
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 1,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: isDarkMode ? "#fff" : "#111" }} noWrap>
                      {n.title || "Notification"}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 14, color: isDarkMode ? "#94a3b8" : "#64748b", whiteSpace: "nowrap" }}>
                      {fmtWhen(n)}
                    </Typography>

                    {isUnread && (
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: 999,
                          flexShrink: 0,
                          bgcolor: "#3B82F6",
                          boxShadow: isDarkMode
                            ? "0 0 0 2px rgba(59,130,246,0.25)"
                            : "0 0 0 2px rgba(59,130,246,0.15)",
                        }}
                      />
                    )}
                  </Box>
                </Box>

              {/* expanded content only (selectable, not clickable for collapse) */}
              <Collapse in={isExpanded} timeout={200}>
                {(n.text || n.body || n.actionUrl) && (
                  <Box sx={{ mt: 1.6, cursor: "text", userSelect: "text" }}>
                    {(n.text || n.body) && (
                      <ScrollableMessageBody fadeBg={cardBg}>
                        {agentTaskContent ? (
                          <AgentTaskNotificationContent notification={n} />
                        ) : (
                          <>
                            {n.text && renderRichText(n.text)}
                            {n.body && (
                              <Box sx={{ mt: n.text ? 1 : 0 }}>
                                {renderRichText(n.body)}
                              </Box>
                            )}
                          </>
                        )}
                      </ScrollableMessageBody>
                    )}

                    {n.actionUrl && (
                      <Box sx={{ mt: 1.25 }}>
                        <Button
                          component="a"
                          href={normalizedActionUrl(n.actionUrl)}
                          target={
                            /^https?:\/\//i.test(normalizedActionUrl(n.actionUrl))
                              ? "_blank"
                              : undefined
                          }
                          rel={
                            /^https?:\/\//i.test(normalizedActionUrl(n.actionUrl))
                              ? "noreferrer"
                              : undefined
                          }
                          variant="contained"
                          size="small"
                          sx={{ textTransform: "none", borderRadius: "999px" }}
                        >
                          Open
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </Collapse>
              </Box>
            </Box>
          );
        })}

        {hasMore && (
          <Button
            onClick={() => load({ reset: false })}
            disabled={loading}
            sx={{ textTransform: "none", borderRadius: 999 }}
            variant="contained"
            fullWidth
          >
            {loading ? "Loading..." : "Load more"}
          </Button>
        )}
      </Box>
    </Box>
  );
}
